import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    UserLicenseDTO,
    useSearchUserLicensesApi,
} from '../../../api/user-license-api';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { T } from '../../../components/basic/text';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { ManageUserLicenseModal } from './manage-user-license-modal';

export function UserLicenseApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState<UserLicenseDTO | undefined>();
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [brokenFilter, setBrokenFilter] = useState<boolean | undefined>();
    const licenseInit = useSearchUserLicensesApi();

    const { result, error, busy, initialized, initializing } = licenseInit;

    const columns: IBasicTableColumn<UserLicenseDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 60,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>user</T>,
                name: 'userName',
                width: 150,
            },
            {
                Header: <T>product</T>,
                name: 'productName',
                minWidth: 150,
            },
            {
                Header: <T>license-code</T>,
                name: 'licenseCode',
                width: 150,
            },
            {
                Header: <T>download-link</T>,
                name: 'downloadLink',
                minWidth: 200,
                Cell: ({ row }) => (
                    <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                        {row.downloadLink || '-'}
                    </span>
                ),
            },
            {
                Header: <T>downloads</T>,
                name: 'downloadCount',
                width: 100,
                Cell: ({ row }) => (
                    <span>
                        {row.downloadCount} / {row.maxDownloads}
                    </span>
                ),
            },
            {
                Header: <T>status</T>,
                name: 'status',
                width: 120,
                Cell: ({ row }) => {
                    const color =
                        row.status === 'Active'
                            ? 'success'
                            : row.status === 'Expired'
                            ? 'warning'
                            : row.status === 'Suspended'
                            ? 'danger'
                            : 'secondary';
                    return (
                        <bd.Badge variant={color} size="sm">
                            {row.status}
                        </bd.Badge>
                    );
                },
            },
            {
                Header: <T>broken</T>,
                name: 'isBroken',
                width: 80,
                Cell: ({ row }) => (
                    <bd.Badge variant={row.isBroken ? 'danger' : 'success'} size="sm">
                        {row.isBroken ? <T>yes</T> : <T>no</T>}
                    </bd.Badge>
                ),
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        licenseInit.post(null, {});
    }, [initialized, initializing, error, licenseInit]);

    const handleFilterChange = (status?: string, broken?: boolean) => {
        setStatusFilter(status || '');
        setBrokenFilter(broken);
        licenseInit.post(null, {
            status: status || undefined,
            isBroken: broken,
        });
    };

    if (error) return <InitFailed ex={error} onRetry={() => licenseInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="user-licenses-management"
                    controls={
                        <div className="d-flex gap-2">
                            <bd.ButtonGroup size="sm">
                                <bd.Button
                                    variant={!statusFilter && !brokenFilter ? 'contained' : 'outline'}
                                    onClick={() => handleFilterChange()}
                                >
                                    <T>all</T>
                                </bd.Button>
                                <bd.Button
                                    variant={statusFilter === 'Active' ? 'contained' : 'outline'}
                                    onClick={() => handleFilterChange('Active')}
                                >
                                    <T>active</T>
                                </bd.Button>
                                <bd.Button
                                    variant={statusFilter === 'Expired' ? 'contained' : 'outline'}
                                    onClick={() => handleFilterChange('Expired')}
                                >
                                    <T>expired</T>
                                </bd.Button>
                                <bd.Button
                                    variant={brokenFilter === true ? 'contained' : 'outline'}
                                    color={brokenFilter ? 'secondary' : 'primary'}
                                    onClick={() => handleFilterChange('', true)}
                                >
                                    <T>broken-only</T>
                                </bd.Button>
                            </bd.ButtonGroup>
                        </div>
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        cellCSS={({ row }) => {
                            if (row.isBroken) return 'bg-red-50';
                            if (row.status === 'Expired') return 'bg-gray-100 opacity-75';
                            return '';
                        }}
                        columns={columns}
                        data={result.licenses || []}
                        onRowClick={({ row }) => {
                            setSelectedLicense(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <ManageUserLicenseModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedLicense(undefined);
                        }}
                        selectedLicense={selectedLicense}
                        onUpdate={() => licenseInit.post(null, { status: statusFilter, isBroken: brokenFilter })}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}

export default UserLicenseApp;

